import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import type {
  Case,
  CaseDocument,
  CaseEvent,
  CaseLink,
  CaseStatus,
  CaseType,
  Client,
  EventType,
  Tag,
} from '@core/domain/model';
import { dayMonthTime, numeric, shortDayMonth } from '@core/ui/RuDates';
import { typeAccent, typeLabel } from './formatter';
import type { CaseAccent } from './types';
import { telemetry } from '@core/telemetry';

/** Строка документа в карточке: «PDF · 9 стр. · 15.08.2026». */
export interface DocumentRow {
  id: string;
  title: string;
  meta: string;
  isFavorite: boolean;
}

export type EventKind = 'hearing' | 'meeting' | 'task' | 'overdue';

export interface EventRow {
  id: string;
  title: string;
  /** «14 сентября, 10:30» либо «Просрочено — до 27 авг». */
  timing: string;
  kind: EventKind;
}

export interface CaseDetailState {
  isLoading: boolean;
  /** Дело удалено или открыто по устаревшей ссылке. */
  notFound: boolean;
  id: string;
  title: string;
  status: CaseStatus;
  statusLabel: string;
  statusAccent: CaseAccent;
  type: CaseType;
  typeLabel: string;
  typeAccent: ReturnType<typeof typeAccent>;
  typeOther: string;
  description: string | null;
  notes: string | null;
  client: { id: string; name: string; phone: string | null } | null;
  knownClients: { id: string; name: string }[];
  tags: Tag[];
  knownTags: Tag[];
  links: CaseLink[];
  documents: DocumentRow[];
  documentsTotal: number;
  events: EventRow[];
  isPinned: boolean;
  /**
   * Дело удалено и ждёт «Отменить» ([D-12]). Карточка остаётся на экране: уйти с неё
   * раньше, чем юрист успел передумать, — значит отнять отмену.
   */
  isDeleted: boolean;
}

const STATUS_LABEL: Record<CaseStatus, string> = {
  Active: 'Активное',
  Suspended: 'Приостановлено',
  Completed: 'Завершено',
};

const STATUS_ACCENT: Record<CaseStatus, CaseAccent> = {
  Active: 'active',
  Suspended: 'paused',
  Completed: 'completed',
};

/** «PDF · 9 стр. · 15.08.2026» — тип файла, объём и дата одной строкой. */
function documentMeta(item: CaseDocument): string {
  const kind = item.mimeType.includes('pdf')
    ? 'PDF'
    : item.mimeType.startsWith('image/')
      ? 'Фото'
      : 'Файл';

  const pages = item.pageCount ? `${item.pageCount} стр.` : null;
  const size = item.sizeBytes > 0 ? `${Math.max(1, Math.round(item.sizeBytes / 1024))} КБ` : null;

  return [kind, pages ?? size, numeric(new Date(item.addedAt))]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

function toEventRow(event: CaseEvent, now: number): EventRow {
  const at = event.deadlineAt ?? event.startAt;
  const overdue = !event.isCompleted && at < now;

  const kind: EventKind = overdue
    ? 'overdue'
    : event.type === 'Hearing'
      ? 'hearing'
      : event.type === 'Meeting'
        ? 'meeting'
        : 'task';

  const timing = overdue
    ? `Просрочено — до ${shortDayMonth(new Date(at))}`
    : dayMonthTime(new Date(at));

  return { id: event.id, title: event.title, timing, kind };
}

/** Сколько документов показывать в карточке: остальные открываются кнопкой «Все». */
const DOCUMENTS_PREVIEW = 3;

/**
 * Данные карточки дела.
 *
 * Карточка — главный экран приложения: сюда юрист попадает из списка и отсюда уходит
 * в документы, к клиенту и в события. Всё правится на месте, каждое действие сразу
 * перечитывает дело: карточка не должна показывать вчерашнее состояние.
 */
export function useCaseDetail(db: Repositories, caseId: string, now: () => number = Date.now) {
  const [item, setItem] = useState<Case | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [knownTags, setKnownTags] = useState<Tag[]>([]);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [isDeleted, setDeleted] = useState(false);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  /*
   * Открытие **существующего** дела — главный индикатор того, что приложением
   * действительно пользуются, а не завели одно дело и забыли (docs/TELEMETRY.md).
   *
   * Отдельным эффектом, а не внутри загрузки: та перезапускается на каждое обновление
   * карточки — добавил тег, поставил задачу, — и открытий насчиталось бы вдесятеро
   * больше, чем было.
   */
  useEffect(() => {
    telemetry.record('caseOpened');
  }, [caseId]);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const found = await db.cases.find(caseId);
      const ownTags = await db.tags.ofCase(caseId);
      const allTags = await db.tags.all();
      const ownDocuments = await db.documents.ofCase(caseId);
      const ownEvents = await db.events.ofCase(caseId);
      const allClients = await db.clients.list();
      const owner = found?.clientId ? await db.clients.find(found.clientId) : null;

      if (dropped) return;

      setItem(found);
      setClient(owner);
      setClients(allClients);
      setTags(ownTags);
      setKnownTags(allTags);
      setDocuments(ownDocuments);
      setEvents(ownEvents);
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, caseId, version]);

  const state: CaseDetailState = useMemo(() => {
    const moment = now();

    return {
      isLoading,
      notFound: !isLoading && !item && !isDeleted,
      id: caseId,
      title: item?.title ?? '',
      status: item?.status ?? 'Active',
      statusLabel: STATUS_LABEL[item?.status ?? 'Active'],
      statusAccent: STATUS_ACCENT[item?.status ?? 'Active'],
      type: item?.type ?? 'Other',
      typeLabel: item ? typeLabel(item) : '',
      typeAccent: typeAccent(item?.type ?? 'Other'),
      typeOther: item?.typeOther ?? '',
      description: item?.description ?? null,
      notes: item?.notes ?? null,
      client: client ? { id: client.id, name: client.fullName, phone: client.phone } : null,
      knownClients: clients.map((value) => ({ id: value.id, name: value.fullName })),
      tags,
      knownTags,
      links: item?.links ?? [],
      // Избранные документы стоят первыми — репозиторий уже отдал их в этом порядке.
      documents: documents.slice(0, DOCUMENTS_PREVIEW).map((value) => ({
        id: value.id,
        title: value.title,
        meta: documentMeta(value),
        isFavorite: value.isFavorite,
      })),
      documentsTotal: documents.length,
      events: events
        .filter((event) => !event.isCompleted)
        .map((event) => toEventRow(event, moment)),
      isPinned: item?.isPinned ?? false,
      isDeleted,
    };
  }, [caseId, item, client, clients, tags, knownTags, documents, events, isLoading, isDeleted, now]);

  /**
   * Записывает правку дела — **только изменённые поля**.
   *
   * Не собирает дело из того, что лежит в состоянии экрана: пока правка идёт от него,
   * соседняя правка успевает записаться и теряется. Описание, тег и ссылка, поставленные
   * подряд, оставляли в деле одну последнюю запись — поймано 04.09.2026.
   */
  const save = useCallback(
    async (change: Parameters<typeof db.cases.patch>[1]) => {
      await db.cases.patch(caseId, change);
      refresh();
    },
    [db, caseId, refresh],
  );

  const actions = useMemo(
    () => ({
      setTitle: (title: string) => save({ title }),
      setDescription: (description: string) => save({ description: description.trim() || null }),
      setNotes: (notes: string) => save({ notes: notes.trim() || null }),
      setStatus: (status: CaseStatus) => save({ status }),
      setType: (type: CaseType, typeOther: string | null) => save({ type, typeOther }),
      setClient: (clientId: string | null) => save({ clientId }),

      /**
       * Заводит клиента и сразу ставит его этому делу.
       *
       * Клиента дела чаще всего вписывают тогда же, когда впервые о нём заходит речь,
       * — а его ещё нет в приложении. Тот же случай, что при создании дела.
       */
      async createClient(draft: { fullName: string; phone: string | null; email: string | null }) {
        const fullName = draft.fullName.trim();
        if (fullName.length === 0) return;

        const created = await db.clients.create({
          id: '',
          fullName,
          phone: draft.phone?.trim() || null,
          email: draft.email?.trim() || null,
          birthDate: null,
          address: null,
          notes: null,
        });
        if (created.ok) {
          telemetry.record('clientCreated');
          await save({ clientId: created.value });
        } else refresh();
      },
      setLinks: (links: CaseLink[]) => save({ links }),

      async togglePinned() {
        await db.cases.setPinned(caseId, !(item?.isPinned ?? false));
        refresh();
      },

      /**
       * Заводит событие внутри дела: заседание назначают, глядя в дело, а не открывая
       * календарь. Дело подставлено — выбирать его незачем.
       */
      async createEvent(draft: {
        title: string;
        type: EventType;
        startAt: number;
      }): Promise<void> {
        const title = draft.title.trim();
        if (title.length === 0) return;

        await db.events.create({
          id: '',
          title,
          type: draft.type,
          startAt: draft.startAt,
          endAt: null,
          description: null,
          caseId,
          clientId: null,
          reminderMinutesBefore: null,
          isCompleted: false,
          deadlineAt: null,
        });
        telemetry.record('eventCreated');
        refresh();
      },

      async addTag(name: string) {
        await db.tags.attach(caseId, name);
        refresh();
      },

      async removeTag(tagId: string) {
        await db.tags.detach(caseId, tagId);
        refresh();
      },

      /**
       * Удаление — единственное необратимое действие, и оно уносит документы и события.
       * Поэтому подтверждение диалогом (D-12), а после — «Отменить» шесть секунд.
       */
      async remove() {
        await db.cases.remove(caseId);
        setDeleted(true);
        refresh();
      },

      async restore() {
        await db.cases.restore(caseId);
        setDeleted(false);
        refresh();
      },

      refresh,
    }),
    [db, caseId, item, refresh, save],
  );

  return { state, actions };
}
